import { useEffect, useMemo, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ChevronDown, Search } from 'lucide-react';
import { cn } from './utils';
import { COUNTRIES, getCountryByIso, getFlagUrl, type Country } from './countries';
import { DIAL_BY_ISO2, getDialCode } from './phone-codes';
import './phone-country-picker.css';

/**
 * PhoneCountryPicker — modal dial-code selector with flag avatar pill.
 *
 * Trigger: a 48px pill containing a circular flag avatar + the dial
 * code prefix (e.g. "+39"). Designed to sit on the LEFT of a phone
 * number input; the consumer composes them in a flex row.
 *
 * Modal: Pattern B background (token-driven, hue-neutral), search +
 * scrollable list of (flag + "+dial" + country name). Selecting a
 * row updates `value` (the ISO2 code) and closes the dialog.
 *
 * Why a separate primitive (and not a `CountryPicker` variant):
 *   The trigger and list rows have a distinct shape (compact pill
 *   showing a numeric prefix vs. labelled cell with full name).
 *   A render-prop or variant on CountryPicker would muddy the API
 *   for both consumers. Keep two thin primitives now; DRY when a
 *   third picker with the same shape appears.
 *
 * Coverage: only countries that appear in BOTH `COUNTRIES`
 * (lib/countries.ts) AND `DIAL_BY_ISO2` (lib/phone-codes.ts) are
 * shown. If you add a country to one, add it to the other too.
 */

export interface PhoneCountryPickerProps {
  /** ISO 3166-1 alpha-2, uppercase. The selected country. */
  value: string;
  /** Called with the new ISO 3166-1 alpha-2 (uppercase). */
  onChange: (iso2: string) => void;
  /** Disable the trigger (e.g. while parent form is saving). */
  disabled?: boolean;
  /** Forwarded to the trigger button. */
  className?: string;
}

interface PhoneOption {
  country: Country;
  dial: string;
}

const PHONE_OPTIONS: ReadonlyArray<PhoneOption> = COUNTRIES.map((c) => ({
  country: c,
  dial: DIAL_BY_ISO2[c.iso2] ?? '',
})).filter((o): o is PhoneOption => o.dial.length > 0);

export function PhoneCountryPicker({
  value,
  onChange,
  disabled,
  className,
}: PhoneCountryPickerProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const selected = getCountryByIso(value);
  const dial = getDialCode(value) ?? '';

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'flex h-control items-center gap-2 rounded-md bg-foreground/5 px-3 text-left transition-colors hover:bg-foreground/10 focus-visible:bg-foreground/10 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
          aria-label={selected ? `Prefisso ${selected.name} (+${dial})` : 'Prefisso'}
        >
          <FlagAvatar iso2={selected?.iso2 ?? null} />
          <span className="text-sm font-medium tabular-nums text-foreground">
            +{dial || '—'}
          </span>
          <ChevronDown
            className="h-4 w-4 flex-none text-muted-foreground"
            aria-hidden
          />
        </button>
      </DialogPrimitive.Trigger>

      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          data-phone-modal-overlay
          className="fixed inset-0 z-overlay backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        {/* Height contract: target h-[32rem] (mirror of CountryPicker) so
            the popup feels stable while the user types — without the
            fixed height the flex container shrinks to fit its filtered
            children and the modal "jumps" on every keystroke. The
            `max-h-[80vh]` cap kicks in only on very short viewports. */}
        <DialogPrimitive.Content
          data-phone-modal
          className="fixed left-1/2 top-1/2 z-modal flex h-[32rem] max-h-[80vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 flex-col gap-3 rounded-2xl border border-border/10 p-4 text-popover-foreground shadow-xl"
        >
          <DialogPrimitive.Title className="text-lg font-semibold text-foreground">
            Prefisso telefonico
          </DialogPrimitive.Title>
          <PhoneCountryListBody
            selectedIso={value}
            onSelect={(iso2) => {
              onChange(iso2);
              setOpen(false);
            }}
          />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function PhoneCountryListBody({
  selectedIso,
  onSelect,
}: {
  selectedIso: string;
  onSelect: (iso2: string) => void;
}): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return PHONE_OPTIONS;
    return PHONE_OPTIONS.filter(
      (o) =>
        o.country.name.toLowerCase().includes(q) ||
        o.country.iso2.toLowerCase().includes(q) ||
        o.dial.includes(q.replace(/^\+/, '')),
    );
  }, [query]);

  return (
    <>
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <input
          ref={inputRef}
          type="text"
          placeholder="Cerca"
          autoComplete="off"
          spellCheck={false}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="h-10 w-full rounded-md bg-foreground/5 pl-9 pr-3 text-sm text-foreground outline-none transition-colors hover:bg-foreground/10 focus-visible:bg-foreground/10"
        />
      </div>

      <ul className="-mr-1 flex flex-1 flex-col overflow-y-auto pr-1">
        {filtered.length === 0 ? (
          <li className="px-3 py-2.5 text-sm text-muted-foreground">
            Nessun prefisso.
          </li>
        ) : (
          filtered.map((o) => (
            <PhoneCountryRow
              key={o.country.iso2}
              option={o}
              selected={o.country.iso2 === selectedIso}
              onSelect={() => onSelect(o.country.iso2)}
            />
          ))
        )}
      </ul>
    </>
  );
}

function PhoneCountryRow({
  option,
  selected,
  onSelect,
}: {
  option: PhoneOption;
  selected: boolean;
  onSelect: () => void;
}): JSX.Element {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          'flex w-full items-center gap-3 rounded-md px-2 py-2 text-left text-sm transition-colors focus:outline-none',
          selected
            ? 'bg-foreground/5 text-foreground'
            : 'text-foreground hover:bg-foreground/10 focus:bg-foreground/10',
        )}
      >
        <FlagAvatar iso2={option.country.iso2} />
        <span className="w-14 flex-none tabular-nums font-medium text-foreground">
          +{option.dial}
        </span>
        <span className="flex-1 truncate text-foreground">{option.country.name}</span>
      </button>
    </li>
  );
}

function FlagAvatar({ iso2 }: { iso2: string | null }): JSX.Element {
  const [failed, setFailed] = useState(false);
  return (
    <div className="relative h-7 w-7 flex-none">
      {iso2 && !failed ? (
        <img
          src={getFlagUrl(iso2)}
          alt=""
          loading="lazy"
          onError={() => setFailed(true)}
          className="h-full w-full rounded-full object-cover"
        />
      ) : (
        <div className="h-full w-full rounded-full bg-foreground/10" />
      )}
    </div>
  );
}
